import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
// NOTE: Stripe refunds are now processed via Firebase Functions (secureStripeService.processRefund)
// to avoid exposing the Stripe secret key client-side. The stripe.ts file should not be used
// from client-side code.

// Types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'customer' | 'technician' | 'admin';
  phone?: string;
  address?: string;
  password?: string;
  createdAt: Timestamp;
  isActive: boolean;
}

// Pool Interfaces
export interface BookingPool {
  id: string;
  name: string;
  poolAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  city: string;
  state: string;
  street: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  label: string;
  startTime: Timestamp;
  type: string;
  status: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  customerId: string;
}

export interface SavedAddress {
  id?: string;
  customerId?: string;
  label?: string;
  street?: string;
  line1?: string; // Alternative to street
  line2?: string;
  city: string;
  state: string;
  zipCode: string;
  latitude?: number;
  longitude?: number;
  createdAt?: Timestamp;
}

// Service selection for multi-service bookings
export interface ServiceSelection {
  packageId: string; // Format: "serviceTypeId:variantId" or "serviceTypeId"
  addOns?: string[]; // Array of add-on/service extra IDs
  vehicleCount?: number; // Number of vehicles for this service
  quantity?: number; // Quantity of this service (default: 1)
}

export interface Booking {
  id: string;
  customerId: string;
  customerName: string;
  serviceType: string; // Legacy: single service (for backward compatibility)
  variantId?: string; // Service variant ID (if variant was selected)
  addOns?: string[]; // Array of add-on/service extra IDs
  services?: ServiceSelection[]; // New: multiple services support
  scheduledDate: Timestamp;
  scheduledTime?: Timestamp;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  amount: number;
  address: SavedAddress | string; // Can be object or legacy string
  addressId?: string; // Reference to addresses collection document ID
  notes?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  technicianId?: string;
  rating?: number; // Customer rating (1-5)
  // Vehicle fields - support both single and multiple
  vehicleId?: string; // Legacy: single vehicle ID
  vehicleInfo?: { // Legacy: single vehicle info
    make?: string;
    model?: string;
    year?: number;
    color?: string;
    licensePlate?: string;
  };
  vehicles?: Array<{ // New: multiple vehicles support
    vehicleId?: string;
    vehicleInfo?: {
      make?: string;
      model?: string;
      year?: number;
      color?: string;
      licensePlate?: string;
    };
  }>;
  // Pool-related fields
  poolId?: string;
  poolName?: string;
  isPoolBooking: boolean;
  originalAmount?: number; // Amount before pool discount
  poolDiscount?: number; // Discount amount applied
  poolDiscountPercentage?: number; // Discount percentage
  // Payment fields
  paymentStatus?: 'pending' | 'partial' | 'deposit_paid' | 'paid' | 'refunded' | 'failed';
  amountPaid?: number;
  remainingAmount?: number;
  paymentNotes?: string; // Notes specific to payment (for non-Stripe payments, manual adjustments, etc.)
  customerContact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

export interface Payment {
  id: string;
  bookingId: string;
  customerId: string;
  customerName?: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'partially_refunded';
  paymentMethod: string;
  method?: string; // Alias for paymentMethod for backward compatibility
  transactionId?: string;
  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string; // Added for Checkout Session payments
  stripeRefundId?: string;
  refundAmount?: number;
  refundReason?: string;
  refundedAt?: Timestamp;
  refundedBy?: string;
  notes?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// Service Types Interfaces
export interface ServiceVariant {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration?: number; // in minutes
  isActive: boolean;
}

export interface ServiceExtra {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration?: number; // additional time in minutes
  isActive: boolean;
  category?: string; // e.g., 'interior', 'exterior', 'premium'
}

export interface ServiceType {
  id: string;
  name: string;
  description?: string;
  category: string;
  basePrice?: number;
  variants: ServiceVariant[];
  extras: ServiceExtra[];
  features?: string[]; // Array of feature strings
  isActive: boolean;
  sortOrder: number;
  imageUrl?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// Technician Interfaces
export interface TechnicianLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: Timestamp;
  address?: string;
}

export interface ServiceArea {
  id: string;
  name: string;
  center: {
    latitude: number;
    longitude: number;
  };
  radius: number; // in kilometers
  isActive: boolean;
  color?: string;
}

export interface TechnicianPerformance {
  totalBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  averageRating: number;
  totalEarnings: number;
  averageServiceTime: number; // in minutes
  customerSatisfaction: number; // percentage
  onTimePercentage: number; // percentage
  lastMonthBookings: number;
  lastMonthEarnings: number;
}

export interface Technician {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profileImage?: string;
  isActive: boolean;
  isOnline: boolean;
  currentLocation?: TechnicianLocation;
  serviceAreas: ServiceArea[];
  skills: string[]; // service types they can perform
  hourlyRate: number;
  rating: number;
  totalReviews: number;
  joinDate: Timestamp;
  lastActive: Timestamp;
  performance: TechnicianPerformance;
  notes?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  vehicleInfo?: {
    make: string;
    model: string;
    year: number;
    color: string;
    licensePlate: string;
  };
  documents?: {
    driverLicense: string;
    insurance: string;
    backgroundCheck: string;
    certifications: string[];
  };
  schedule?: {
    monday: { start: string; end: string; isAvailable: boolean };
    tuesday: { start: string; end: string; isAvailable: boolean };
    wednesday: { start: string; end: string; isAvailable: boolean };
    thursday: { start: string; end: string; isAvailable: boolean };
    friday: { start: string; end: string; isAvailable: boolean };
    saturday: { start: string; end: string; isAvailable: boolean };
    sunday: { start: string; end: string; isAvailable: boolean };
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// User Services
export const getUserById = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

export const getAllUsers = async (): Promise<User[]> => {
  try {
    const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Get active customers only (for booking form)
export const getActiveCustomers = async (): Promise<User[]> => {
  try {
    const customersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'customer'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(customersQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
  } catch (error) {
    console.error('Error fetching active customers:', error);
    throw error;
  }
};

// Vehicle interface for customer vehicles
export interface CustomerVehicle {
  id: string;
  customerId: string;
  make: string;
  model: string;
  year?: number;
  color?: string;
  licensePlate?: string;
  type?: 'sedan' | 'suv' | 'truck' | 'van';
  createdAt?: Timestamp;
  isActive?: boolean;
}

// Get customer addresses
export const getCustomerAddresses = async (customerId: string): Promise<SavedAddress[]> => {
  try {
    const addressesQuery = query(
      collection(db, 'addresses'),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(addressesQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as SavedAddress[];
  } catch (error) {
    console.error('Error fetching customer addresses:', error);
    throw error;
  }
};

// Get customer vehicles
export const getCustomerVehicles = async (customerId: string): Promise<CustomerVehicle[]> => {
  try {
    const vehiclesQuery = query(
      collection(db, 'vehicles'),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(vehiclesQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as CustomerVehicle[];
  } catch (error) {
    console.error('Error fetching customer vehicles:', error);
    throw error;
  }
};

export const createUser = async (userData: Omit<User, 'id' | 'createdAt'>, uid?: string): Promise<User> => {
  try {
    const userToCreate = {
      ...userData,
      createdAt: Timestamp.now(),
      isActive: userData.isActive !== false
    };
    
    if (uid) {
      // Use the provided UID as the document ID
      await setDoc(doc(db, 'users', uid), userToCreate);
      return { id: uid, ...userToCreate } as User;
    } else {
      // Generate a random document ID (for backward compatibility)
      const docRef = await addDoc(collection(db, 'users'), userToCreate);
      return { id: docRef.id, ...userToCreate } as User;
    }
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (userId: string, userData: Partial<User>): Promise<void> => {
  try {
    const { id, createdAt, ...updateData } = userData;
    await updateDoc(doc(db, 'users', userId), updateData);
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (userId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'users', userId));
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const updateUserRole = async (userId: string, role: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', userId), { role });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// Booking Services
export const getAllBookings = async (): Promise<Booking[]> => {
  try {
    const bookingsQuery = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(bookingsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Booking);
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

export const getBookingById = async (bookingId: string): Promise<Booking | null> => {
  try {
    const bookingDoc = await getDoc(doc(db, 'bookings', bookingId));
    if (bookingDoc.exists()) {
      return { id: bookingDoc.id, ...bookingDoc.data() } as Booking;
    }
    return null;
  } catch (error) {
    console.error('Error fetching booking:', error);
    throw error;
  }
};

export const updateBookingStatus = async (bookingId: string, status: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'bookings', bookingId), { status });
  } catch (error) {
    console.error('Error updating booking status:', error);
    throw error;
  }
};

export const updateBooking = async (bookingId: string, bookingData: Partial<Booking>): Promise<void> => {
  try {
    // Remove fields that shouldn't be updated (id, createdAt, and any joined/computed fields like 'customer')
    const { id, createdAt, customer, ...updateData } = bookingData as any;
    
    // CRITICAL: Preserve address object structure - don't overwrite with string
    // If address is being updated, ensure it's an object, not a string
    if ('address' in updateData && updateData.address !== undefined) {
      // If address is a string, don't update it (preserve existing address object)
      if (typeof updateData.address === 'string') {
        console.warn('Address update skipped: address should be an object, not a string');
        delete updateData.address; // Remove string address to preserve existing object
      } else if (typeof updateData.address === 'object' && updateData.address !== null) {
        // Normalize address object - ensure latitude/longitude are numbers
        const addr = updateData.address as any;
        if (addr.latitude !== undefined && addr.latitude !== null) {
          addr.latitude = typeof addr.latitude === 'string' ? parseFloat(addr.latitude) : Number(addr.latitude);
          if (isNaN(addr.latitude)) delete addr.latitude;
        }
        if (addr.longitude !== undefined && addr.longitude !== null) {
          addr.longitude = typeof addr.longitude === 'string' ? parseFloat(addr.longitude) : Number(addr.longitude);
          if (isNaN(addr.longitude)) delete addr.longitude;
        }
        updateData.address = addr;
      }
    }
    
    // Remove any undefined values - Firestore doesn't accept undefined
    const cleanUpdateData: any = {};
    for (const [key, value] of Object.entries(updateData)) {
      if (value !== undefined) {
        cleanUpdateData[key] = value;
      }
    }
    
    await updateDoc(doc(db, 'bookings', bookingId), cleanUpdateData);
  } catch (error) {
    console.error('Error updating booking:', error);
    throw error;
  }
};

export const createBooking = async (bookingData: Omit<Booking, 'id' | 'createdAt'>): Promise<Booking> => {
  try {
    const bookingToCreate = {
      ...bookingData,
      createdAt: Timestamp.now(),
      status: bookingData.status || 'pending'
    };
    
    const docRef = await addDoc(collection(db, 'bookings'), bookingToCreate);
    return { id: docRef.id, ...bookingToCreate } as Booking;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

// Payment Services
export const getAllPayments = async (): Promise<Payment[]> => {
  try {
    const paymentsQuery = query(collection(db, 'paymentIntents'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(paymentsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Payment);
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

export const getPaymentById = async (paymentId: string): Promise<Payment | null> => {
  try {
    const paymentDoc = await getDoc(doc(db, 'paymentIntents', paymentId));
    if (paymentDoc.exists()) {
      return { id: paymentDoc.id, ...paymentDoc.data() } as Payment;
    }
    return null;
  } catch (error) {
    console.error('Error fetching payment:', error);
    throw error;
  }
};

export const updatePayment = async (paymentId: string, paymentData: Partial<Payment>): Promise<void> => {
  try {
    const { id, createdAt, ...updateData } = paymentData;
    
    // Check if this is a payment from bookings collection (ID starts with "booking_")
    if (paymentId.startsWith('booking_')) {
      // Extract the actual booking ID
      const bookingId = paymentId.replace('booking_', '');
      
      // For bookings, we store payment notes in a separate field
      // This allows us to track payment-specific notes separately from booking notes
      const bookingUpdateData: any = {
        updatedAt: Timestamp.now()
      };
      
      // If notes are provided, store them as paymentNotes
      if (updateData.notes !== undefined) {
        bookingUpdateData.paymentNotes = updateData.notes;
      }
      
      // Update the booking document
      await updateDoc(doc(db, 'bookings', bookingId), bookingUpdateData);
    } else {
      // Legacy paymentIntents collection
      await updateDoc(doc(db, 'paymentIntents', paymentId), {
        ...updateData,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Error updating payment:', error);
    throw error;
  }
};

export const processRefund = async (
  paymentId: string, 
  refundAmount: number, // Amount in cents
  refundReason: string, 
  refundedBy: string,
  stripeRefundId?: string // Optional Stripe refund ID if refund was processed via Firebase Functions
): Promise<void> => {
  try {
    const paymentDoc = await getDoc(doc(db, 'paymentIntents', paymentId));
    if (!paymentDoc.exists()) {
      throw new Error('Payment not found');
    }

    const payment = paymentDoc.data() as Payment;
    
    // Calculate existing refund amount (if any)
    const existingRefundAmount = payment.refundAmount || 0;
    const remainingAmount = payment.amount - existingRefundAmount;
    
    if (refundAmount > remainingAmount) {
      throw new Error(`Refund amount cannot exceed remaining amount of $${(remainingAmount / 100).toFixed(2)}`);
    }

    if (payment.status === 'refunded') {
      throw new Error('Payment has already been fully refunded');
    }

    // NOTE: Stripe refunds should be processed via Firebase Functions (secureStripeService.processRefund)
    // NOT here, as this would expose the Stripe secret key client-side.
    // This function only updates Firestore records.
    // The stripeRefundId should be passed in if the refund was already processed via Firebase Functions.

    // Calculate total refunded amount
    const totalRefunded = existingRefundAmount + refundAmount;
    const isFullRefund = totalRefunded >= payment.amount;
    const newStatus = isFullRefund ? 'refunded' : 'partially_refunded';

    await updateDoc(doc(db, 'paymentIntents', paymentId), {
      status: newStatus,
      refundAmount: totalRefunded, // Store total refunded amount
      refundReason: refundReason,
      refundedAt: Timestamp.now(),
      refundedBy: refundedBy,
      stripeRefundId: stripeRefundId || payment.stripeRefundId, // Preserve existing or use new
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error processing refund:', error);
    throw error;
  }
};

export const getPaymentsByStatus = async (status: Payment['status']): Promise<Payment[]> => {
  try {
    const paymentsQuery = query(
      collection(db, 'paymentIntents'), 
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(paymentsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Payment);
  } catch (error) {
    console.error('Error fetching payments by status:', error);
    throw error;
  }
};

export const getPaymentsByCustomer = async (customerId: string): Promise<Payment[]> => {
  try {
    const paymentsQuery = query(
      collection(db, 'paymentIntents'), 
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(paymentsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Payment);
  } catch (error) {
    console.error('Error fetching payments by customer:', error);
    throw error;
  }
};

export const getPaymentsByDateRange = async (startDate: Date, endDate: Date): Promise<Payment[]> => {
  try {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    const paymentsQuery = query(
      collection(db, 'paymentIntents'), 
      where('createdAt', '>=', startTimestamp),
      where('createdAt', '<=', endTimestamp),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(paymentsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Payment);
  } catch (error) {
    console.error('Error fetching payments by date range:', error);
    throw error;
  }
};

// Dashboard Statistics
export const getDashboardStats = async () => {
  try {
    const [usersSnapshot, bookingsSnapshot, paymentsSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'bookings')),
      getDocs(collection(db, 'paymentIntents'))
    ]);

    const totalUsers = usersSnapshot.size;
    const totalBookings = bookingsSnapshot.size;
    const totalRevenue = paymentsSnapshot.docs.reduce((sum, doc) => {
      const payment = doc.data() as Payment;
      return payment.status === 'completed' ? sum + payment.amount : sum;
    }, 0);

    const activeBookings = bookingsSnapshot.docs.filter(doc => {
      const booking = doc.data() as Booking;
      return booking.status === 'confirmed' || booking.status === 'in_progress';
    }).length;

    return {
      totalUsers,
      activeBookings,
      totalRevenue,
      completionRate: totalBookings > 0 ? Math.round((activeBookings / totalBookings) * 100) : 0
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Service Type Services
export const getAllServiceTypes = async (): Promise<ServiceType[]> => {
  try {
    const serviceTypesQuery = query(collection(db, 'serviceTypes'), orderBy('sortOrder', 'asc'));
    const querySnapshot = await getDocs(serviceTypesQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as ServiceType);
  } catch (error) {
    console.error('Error fetching service types:', error);
    throw error;
  }
};

export const getServiceTypeById = async (serviceTypeId: string): Promise<ServiceType | null> => {
  try {
    const serviceTypeDoc = await getDoc(doc(db, 'serviceTypes', serviceTypeId));
    if (serviceTypeDoc.exists()) {
      return { id: serviceTypeDoc.id, ...serviceTypeDoc.data() } as ServiceType;
    }
    return null;
  } catch (error) {
    console.error('Error fetching service type:', error);
    throw error;
  }
};

export const createServiceType = async (serviceTypeData: Omit<ServiceType, 'id' | 'createdAt' | 'updatedAt'>): Promise<ServiceType> => {
  try {
    const serviceTypeToCreate = {
      ...serviceTypeData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      isActive: serviceTypeData.isActive !== false,
      variants: serviceTypeData.variants || [],
      extras: serviceTypeData.extras || [],
      sortOrder: serviceTypeData.sortOrder || 0
    };
    
    const docRef = await addDoc(collection(db, 'serviceTypes'), serviceTypeToCreate);
    return { id: docRef.id, ...serviceTypeToCreate } as ServiceType;
  } catch (error) {
    console.error('Error creating service type:', error);
    throw error;
  }
};

export const updateServiceType = async (serviceTypeId: string, serviceTypeData: Partial<ServiceType>): Promise<void> => {
  try {
    const { id, createdAt, ...updateData } = serviceTypeData;
    await updateDoc(doc(db, 'serviceTypes', serviceTypeId), {
      ...updateData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating service type:', error);
    throw error;
  }
};

export const deleteServiceType = async (serviceTypeId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'serviceTypes', serviceTypeId));
  } catch (error) {
    console.error('Error deleting service type:', error);
    throw error;
  }
};

export const getServiceTypesByCategory = async (category: string): Promise<ServiceType[]> => {
  try {
    const serviceTypesQuery = query(
      collection(db, 'serviceTypes'), 
      where('category', '==', category),
      where('isActive', '==', true),
      orderBy('sortOrder', 'asc')
    );
    const querySnapshot = await getDocs(serviceTypesQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as ServiceType);
  } catch (error) {
    console.error('Error fetching service types by category:', error);
    throw error;
  }
};

export const getActiveServiceTypes = async (): Promise<ServiceType[]> => {
  try {
    const serviceTypesQuery = query(
      collection(db, 'serviceTypes'), 
      where('isActive', '==', true),
      orderBy('sortOrder', 'asc')
    );
    const querySnapshot = await getDocs(serviceTypesQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as ServiceType);
  } catch (error) {
    console.error('Error fetching active service types:', error);
    throw error;
  }
};

// Technician Services
export const getAllTechnicians = async (): Promise<Technician[]> => {
  try {
    const techniciansQuery = query(
      collection(db, 'users'), 
      where('role', '==', 'technician'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(techniciansQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Technician);
  } catch (error) {
    console.error('Error fetching technicians:', error);
    throw error;
  }
};

export const getTechnicianById = async (technicianId: string): Promise<Technician | null> => {
  try {
    const technicianDoc = await getDoc(doc(db, 'users', technicianId));
    if (technicianDoc.exists()) {
      return { id: technicianDoc.id, ...technicianDoc.data() } as Technician;
    }
    return null;
  } catch (error) {
    console.error('Error fetching technician:', error);
    throw error;
  }
};

export const createTechnician = async (technicianData: Omit<Technician, 'id' | 'createdAt' | 'updatedAt'>): Promise<Technician> => {
  try {
    const technicianToCreate = {
      ...technicianData,
      role: 'technician' as const, // Set role to 'technician'
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      isActive: technicianData.isActive !== false,
      isOnline: false,
      rating: 0,
      totalReviews: 0,
      performance: {
        totalBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        averageRating: 0,
        totalEarnings: 0,
        averageServiceTime: 0,
        customerSatisfaction: 0,
        onTimePercentage: 0,
        lastMonthBookings: 0,
        lastMonthEarnings: 0
      },
      serviceAreas: technicianData.serviceAreas || [],
      skills: technicianData.skills || [],
      ...technicianData.performance
    };
    
    const docRef = await addDoc(collection(db, 'users'), technicianToCreate);
    return { id: docRef.id, ...technicianToCreate } as Technician;
  } catch (error) {
    console.error('Error creating technician:', error);
    throw error;
  }
};

export const updateTechnician = async (technicianId: string, technicianData: Partial<Technician>): Promise<void> => {
  try {
    const { id, createdAt, ...updateData } = technicianData;
    await updateDoc(doc(db, 'users', technicianId), {
      ...updateData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating technician:', error);
    throw error;
  }
};

export const deleteTechnician = async (technicianId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'users', technicianId));
  } catch (error) {
    console.error('Error deleting technician:', error);
    throw error;
  }
};

export const updateTechnicianLocation = async (technicianId: string, location: TechnicianLocation): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', technicianId), {
      currentLocation: location,
      lastActive: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating technician location:', error);
    throw error;
  }
};

export const updateTechnicianStatus = async (technicianId: string, isOnline: boolean): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', technicianId), {
      isOnline,
      lastActive: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating technician status:', error);
    throw error;
  }
};

export const getOnlineTechnicians = async (): Promise<Technician[]> => {
  try {
    const techniciansQuery = query(
      collection(db, 'users'), 
      where('role', '==', 'technician'),
      where('isOnline', '==', true),
      where('isActive', '==', true),
      orderBy('lastActive', 'desc')
    );
    const querySnapshot = await getDocs(techniciansQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Technician);
  } catch (error) {
    console.error('Error fetching online technicians:', error);
    throw error;
  }
};

export const getTechniciansByServiceArea = async (latitude: number, longitude: number, radius: number): Promise<Technician[]> => {
  try {
    // This is a simplified version - in production you'd use geospatial queries
    const techniciansQuery = query(
      collection(db, 'users'), 
      where('role', '==', 'technician'),
      where('isActive', '==', true),
      orderBy('lastActive', 'desc')
    );
    const querySnapshot = await getDocs(techniciansQuery);
    const technicians = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Technician);
    
    // Filter by distance (simplified calculation)
    return technicians.filter(technician => {
      if (!technician.currentLocation) return false;
      
      const distance = calculateDistance(
        latitude, longitude,
        technician.currentLocation.latitude,
        technician.currentLocation.longitude
      );
      
      return distance <= radius;
    });
  } catch (error) {
    console.error('Error fetching technicians by service area:', error);
    throw error;
  }
};

export const getTechniciansBySkills = async (skills: string[]): Promise<Technician[]> => {
  try {
    const techniciansQuery = query(
      collection(db, 'users'), 
      where('role', '==', 'technician'),
      where('isActive', '==', true),
      orderBy('lastActive', 'desc')
    );
    const querySnapshot = await getDocs(techniciansQuery);
    const technicians = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Technician);
    
    // Filter by skills
    return technicians.filter(technician => 
      skills.some(skill => technician.skills.includes(skill))
    );
  } catch (error) {
    console.error('Error fetching technicians by skills:', error);
    throw error;
  }
};

export const updateTechnicianPerformance = async (technicianId: string, performance: Partial<TechnicianPerformance>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', technicianId), {
      performance: performance,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating technician performance:', error);
    throw error;
  }
};

// Utility function to calculate distance between two points
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

// Enhanced interfaces with joined customer data
export interface BookingWithCustomer extends Booking {
  customer?: {
    firstName?: string;
    lastName?: string;
    email: string;
    phone?: string;
  };
}

export interface PaymentWithCustomer extends Payment {
  customer?: {
    firstName?: string;
    lastName?: string;
    email: string;
    phone?: string;
  };
  booking?: {
    serviceType: string;
    scheduledDate: Timestamp;
    status: string;
  };
}

// Gift Card Interfaces
export interface GiftCode {
  id: string;
  code: string;
  templateId: string;
  denomination: number; // In cents
  purchasedBy: string;
  purchasedAt?: Timestamp;
  recipientEmail: string;
  recipientName?: string;
  message?: string;
  status: 'pending' | 'sent' | 'redeemed' | 'expired' | 'cancelled';
  sentAt?: Timestamp;
  redeemedBy?: string;
  redeemedAt?: Timestamp;
  redeemedForBookingId?: string;
  expiresAt?: Timestamp;
  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface GiftCodeWithCustomer extends GiftCode {
  purchaser?: User;
  redeemer?: User;
}

// Enhanced functions with customer data joins
export const getAllBookingsWithCustomer = async (): Promise<BookingWithCustomer[]> => {
  try {
    const bookingsQuery = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    const bookingsSnapshot = await getDocs(bookingsQuery);
    
    const bookings: BookingWithCustomer[] = [];
    
    for (const bookingDoc of bookingsSnapshot.docs) {
      const bookingData = bookingDoc.data();
      const booking: BookingWithCustomer = {
        id: bookingDoc.id,
        ...bookingData,
      } as BookingWithCustomer;
      
      // Fetch customer data if customerId exists
      if (booking.customerId) {
        try {
          const customerDoc = await getDoc(doc(db, 'users', booking.customerId));
          if (customerDoc.exists()) {
            const customerData = customerDoc.data();
            booking.customer = {
              firstName: customerData.firstName,
              lastName: customerData.lastName,
              email: customerData.email,
              phone: customerData.phone,
            };
          }
        } catch (error) {
          console.error(`Error fetching customer data for booking ${booking.id}:`, error);
        }
      }
      
      bookings.push(booking);
    }
    
    return bookings;
  } catch (error) {
    console.error('Error fetching bookings with customer data:', error);
    throw error;
  }
};

export const getAllPaymentsWithCustomer = async (): Promise<PaymentWithCustomer[]> => {
  try {
    const payments: PaymentWithCustomer[] = [];
    
    // 1. Fetch payments from paymentIntents collection (legacy Payment Intent payments)
    try {
      const paymentIntentsQuery = query(collection(db, 'paymentIntents'), orderBy('createdAt', 'desc'));
      const paymentIntentsSnapshot = await getDocs(paymentIntentsQuery);
      
      console.log(`📊 Found ${paymentIntentsSnapshot.docs.length} payments in paymentIntents collection`);
      
      for (const paymentDoc of paymentIntentsSnapshot.docs) {
      const paymentData = paymentDoc.data();
      const payment: PaymentWithCustomer = {
        id: paymentDoc.id,
        ...paymentData,
      } as PaymentWithCustomer;
      
      // Fetch customer data if customerId exists
      if (payment.customerId) {
        try {
          const customerDoc = await getDoc(doc(db, 'users', payment.customerId));
          if (customerDoc.exists()) {
            const customerData = customerDoc.data();
            payment.customer = {
              firstName: customerData.firstName,
              lastName: customerData.lastName,
              email: customerData.email,
              phone: customerData.phone,
            };
          }
        } catch (error) {
          console.error(`Error fetching customer data for payment ${payment.id}:`, error);
        }
      }
      
      // Fetch booking data if bookingId exists
      if (payment.bookingId) {
        try {
          const bookingDoc = await getDoc(doc(db, 'bookings', payment.bookingId));
          if (bookingDoc.exists()) {
            const bookingData = bookingDoc.data();
            payment.booking = {
              serviceType: bookingData.serviceType,
              scheduledDate: bookingData.scheduledDate,
              status: bookingData.status,
            };
          }
        } catch (error) {
          console.error(`Error fetching booking data for payment ${payment.id}:`, error);
        }
      }
      
      payments.push(payment);
      }
    } catch (error) {
      console.error('Error fetching paymentIntents:', error);
    }
    
    // 2. Fetch payments from bookings collection (Checkout Session payments)
    // Note: We query all bookings and filter client-side because Firestore doesn't support
    // queries on multiple optional fields efficiently. In production, consider adding a
    // 'hasPayment' boolean field to bookings for better query performance.
    try {
      const bookingsQuery = query(
        collection(db, 'bookings'),
        orderBy('createdAt', 'desc')
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      
      console.log(`📊 Checking ${bookingsSnapshot.docs.length} bookings for payment information`);
      
      const paymentsBeforeBookings = payments.length;
      
      for (const bookingDoc of bookingsSnapshot.docs) {
        const bookingData = bookingDoc.data();
        
        // Only process bookings that have payment information
        // Check for: stripeCheckoutSessionId, amountPaid > 0, amount > 0, or paymentStatus
        const hasPaymentInfo = !!(bookingData.stripeCheckoutSessionId || 
                                  (bookingData.amountPaid && bookingData.amountPaid > 0) || 
                                  (bookingData.amount && bookingData.amount > 0) ||
                                  bookingData.paymentStatus);
        
        if (!hasPaymentInfo) {
          continue; // Skip bookings without payment info
        }
        
        // Transform booking payment data to Payment format
        // Use amountPaid if available (actual payment), otherwise use amount (total booking amount)
        const paymentAmount = bookingData.amountPaid > 0 ? bookingData.amountPaid : (bookingData.amount || 0);
        if (paymentAmount <= 0) {
          continue; // Skip if no payment amount
        }
        
        // Map booking paymentStatus to Payment status
        let paymentStatus: Payment['status'] = 'pending';
        if (bookingData.paymentStatus === 'paid') {
          paymentStatus = 'completed';
        } else if (bookingData.paymentStatus === 'failed') {
          paymentStatus = 'failed';
        } else if (bookingData.paymentStatus === 'refunded') {
          paymentStatus = 'refunded';
        } else if (bookingData.paymentStatus === 'deposit_paid' || bookingData.paymentStatus === 'partial') {
          paymentStatus = 'completed'; // Treat partial/deposit as completed for display
        }
        
        const payment: PaymentWithCustomer = {
          id: `booking_${bookingDoc.id}`, // Prefix to avoid conflicts
          bookingId: bookingDoc.id,
          customerId: bookingData.customerId || '',
          amount: paymentAmount,
          status: paymentStatus,
          paymentMethod: bookingData.stripeCheckoutSessionId ? 'checkout_session' : (bookingData.paymentNotes ? 'manual' : 'unknown'),
          method: bookingData.stripeCheckoutSessionId ? 'checkout_session' : (bookingData.paymentNotes ? 'manual' : 'unknown'),
          transactionId: bookingData.stripeCheckoutSessionId || undefined,
          stripeCheckoutSessionId: bookingData.stripeCheckoutSessionId || undefined,
          createdAt: bookingData.createdAt || Timestamp.now(),
          updatedAt: bookingData.updatedAt || Timestamp.now(),
          notes: bookingData.paymentNotes || undefined, // Use paymentNotes from booking for non-Stripe payments
          // Additional booking payment fields
          remainingAmount: bookingData.remainingAmount || 0,
          paymentStrategy: bookingData.paymentStrategy,
        } as PaymentWithCustomer;
        
        // Fetch customer data
        if (bookingData.customerId) {
          try {
            const customerDoc = await getDoc(doc(db, 'users', bookingData.customerId));
            if (customerDoc.exists()) {
              const customerData = customerDoc.data();
              payment.customer = {
                firstName: customerData.firstName || bookingData.customerContact?.name?.split(' ')[0],
                lastName: customerData.lastName || bookingData.customerContact?.name?.split(' ').slice(1).join(' '),
                email: customerData.email || bookingData.customerContact?.email,
                phone: customerData.phone || bookingData.customerContact?.phone || bookingData.customerContact?.phoneNumber,
              };
            } else if (bookingData.customerContact) {
              // Fallback to customerContact from booking
              const nameParts = (bookingData.customerContact.name || '').split(' ');
              payment.customer = {
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                email: bookingData.customerContact.email || '',
                phone: bookingData.customerContact.phone || bookingData.customerContact.phoneNumber || '',
              };
            }
          } catch (error) {
            console.error(`Error fetching customer data for booking payment ${payment.id}:`, error);
          }
        }
        
        // Booking data is already available
        payment.booking = {
          serviceType: bookingData.serviceType,
          scheduledDate: bookingData.scheduledTime || bookingData.scheduledDate,
          status: bookingData.status,
        };
        
        payments.push(payment);
      }
      
      const bookingPaymentsCount = payments.length - paymentsBeforeBookings;
      console.log(`✅ Added ${bookingPaymentsCount} payments from bookings collection`);
    } catch (error) {
      console.error('Error fetching bookings payments:', error);
    }
    
    // 3. Sort all payments by createdAt (most recent first)
    payments.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || (a.createdAt as any)?.seconds * 1000 || 0;
      const bTime = b.createdAt?.toMillis?.() || (b.createdAt as any)?.seconds * 1000 || 0;
      return bTime - aTime;
    });
    
    console.log(`✅ Total payments loaded: ${payments.length} (from paymentIntents + bookings)`);
    
    return payments;
  } catch (error) {
    console.error('Error fetching payments with customer data:', error);
    throw error;
  }
};

export const getDashboardStatsWithCustomerData = async () => {
  try {
    const [usersSnapshot, bookingsSnapshot, paymentsSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'bookings')),
      getDocs(collection(db, 'paymentIntents')),
    ]);

    const totalUsers = usersSnapshot.size;
    const activeBookings = bookingsSnapshot.docs.filter(
      doc => ['confirmed', 'in_progress'].includes(doc.data().status)
    ).length;
    
    const totalRevenue = paymentsSnapshot.docs
      .filter(doc => doc.data().status === 'completed')
      .reduce((sum, doc) => sum + (doc.data().amount || 0), 0);
    
    const completedBookings = bookingsSnapshot.docs.filter(
      doc => doc.data().status === 'completed'
    ).length;
    
    const totalBookings = bookingsSnapshot.size;
    const completionRate = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;

    return {
      totalUsers,
      activeBookings,
      totalRevenue,
      completionRate,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Pool Services
export const getAllBookingPools = async (): Promise<BookingPool[]> => {
  try {
    const poolsQuery = query(collection(db, 'bookingPools'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(poolsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as BookingPool);
  } catch (error) {
    console.error('Error fetching booking pools:', error);
    throw error;
  }
};

export const getBookingPoolById = async (poolId: string): Promise<BookingPool | null> => {
  try {
    const poolDoc = await getDoc(doc(db, 'bookingPools', poolId));
    if (poolDoc.exists()) {
      return { id: poolDoc.id, ...poolDoc.data() } as BookingPool;
    }
    return null;
  } catch (error) {
    console.error('Error fetching booking pool:', error);
    throw error;
  }
};

export const createBookingPool = async (poolData: Omit<BookingPool, 'id' | 'createdAt' | 'updatedAt' | 'currentBookings' | 'totalRevenue' | 'averageBookingValue'>): Promise<BookingPool> => {
  try {
    const now = Timestamp.now();
    const newPool = {
      ...poolData,
      currentBookings: 0,
      totalRevenue: 0,
      averageBookingValue: 0,
      createdAt: now,
      updatedAt: now,
    };
    
    const docRef = await addDoc(collection(db, 'bookingPools'), newPool);
    return { id: docRef.id, ...newPool } as BookingPool;
  } catch (error) {
    console.error('Error creating booking pool:', error);
    throw error;
  }
};

export const updateBookingPool = async (poolId: string, poolData: Partial<BookingPool>): Promise<void> => {
  try {
    const poolRef = doc(db, 'bookingPools', poolId);
    await updateDoc(poolRef, {
      ...poolData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating booking pool:', error);
    throw error;
  }
};

export const deleteBookingPool = async (poolId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'bookingPools', poolId));
  } catch (error) {
    console.error('Error deleting booking pool:', error);
    throw error;
  }
};

export const getBookingPoolsByStatus = async (status: BookingPool['status']): Promise<BookingPool[]> => {
  try {
    const poolsQuery = query(
      collection(db, 'bookingPools'),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(poolsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as BookingPool);
  } catch (error) {
    console.error('Error fetching booking pools by status:', error);
    throw error;
  }
};

export const getBookingPoolsByServiceType = async (serviceType: string): Promise<BookingPool[]> => {
  try {
    const poolsQuery = query(
      collection(db, 'bookingPools'),
      where('serviceType', '==', serviceType),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(poolsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as BookingPool);
  } catch (error) {
    console.error('Error fetching booking pools by service type:', error);
    throw error;
  }
};

export const getActiveBookingPools = async (): Promise<BookingPool[]> => {
  try {
    const poolsQuery = query(
      collection(db, 'bookingPools'),
      where('status', 'in', ['forming', 'confirmed']),
      orderBy('targetDate', 'asc')
    );
    const querySnapshot = await getDocs(poolsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as BookingPool);
  } catch (error) {
    console.error('Error fetching active booking pools:', error);
    throw error;
  }
};

export const getBookingsByPool = async (poolId: string): Promise<Booking[]> => {
  try {
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('poolId', '==', poolId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(bookingsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Booking);
  } catch (error) {
    console.error('Error fetching bookings by pool:', error);
    throw error;
  }
};

export const addParticipantToPool = async (poolId: string, participant: {
  customerId: string;
  customerName: string;
  email: string;
}): Promise<void> => {
  // This function is deprecated as the BookingPool schema no longer supports participants.
  // You may need to implement participant tracking elsewhere or update the schema if needed.
  throw new Error('addParticipantToPool is not supported with the current BookingPool schema.');
};

export const removeParticipantFromPool = async (poolId: string, customerId: string): Promise<void> => {
  // This function is deprecated as the BookingPool schema no longer supports participants.
  // You may need to implement participant tracking elsewhere or update the schema if needed.
  throw new Error('removeParticipantFromPool is not supported with the current BookingPool schema.');
};

export const getPoolAnalytics = async (): Promise<{
  totalPools: number;
  activePools: number;
  completedPools: number;
  poolsByStatus: Record<string, number>;
  recentPools: BookingPool[];
}> => {
  try {
    const allPools = await getAllBookingPools();
    const totalPools = allPools.length;
    const activePools = allPools.filter(pool => ['forming', 'confirmed'].includes(pool.status)).length;
    const completedPools = allPools.filter(pool => pool.status === 'completed').length;
    const poolsByStatus = allPools.reduce((acc, pool) => {
      acc[pool.status] = (acc[pool.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const recentPools = allPools.slice(0, 10); // Last 10 pools
    return {
      totalPools,
      activePools,
      completedPools,
      poolsByStatus,
      recentPools,
    };
  } catch (error) {
    console.error('Error fetching pool analytics:', error);
    throw error;
  }
};

export const getPoolPerformanceMetrics = async (poolId: string): Promise<{
  pool: BookingPool;
  bookings: Booking[];
  totalRevenue: number;
  averageBookingValue: number;
  participationRate: number;
  completionRate: number;
  customerSatisfaction?: number;
}> => {
  try {
    const pool = await getBookingPoolById(poolId);
    if (!pool) {
      throw new Error('Pool not found');
    }
    const bookings = await getBookingsByPool(poolId);
    const totalRevenue = bookings.reduce((sum, booking) => sum + booking.amount, 0);
    const averageBookingValue = bookings.length > 0 ? totalRevenue / bookings.length : 0;
    // participationRate and completionRate logic may need to be updated based on new schema
    const participationRate = 0;
    const completedBookings = bookings.filter(booking => booking.status === 'completed').length;
    const completionRate = bookings.length > 0 ? (completedBookings / bookings.length) * 100 : 0;
    return {
      pool,
      bookings,
      totalRevenue,
      averageBookingValue,
      participationRate,
      completionRate,
    };
  } catch (error) {
    console.error('Error fetching pool performance metrics:', error);
    throw error;
  }
};

export const getBookingPoolMembers = async (poolId: string): Promise<any[]> => {
  const membersQuery = query(collection(db, 'bookingPoolMembers'), where('poolId', '==', poolId));
  const querySnapshot = await getDocs(membersQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getPaymentsByBookingIds = async (bookingIds: string[]): Promise<any[]> => {
  if (!bookingIds.length) return [];
  // Firestore 'in' queries are limited to 10 items per query
  const chunks = [];
  for (let i = 0; i < bookingIds.length; i += 10) {
    chunks.push(bookingIds.slice(i, i + 10));
  }
  let payments: any[] = [];
  for (const chunk of chunks) {
    const paymentsQuery = query(collection(db, 'paymentIntents'), where('bookingId', 'in', chunk));
    const querySnapshot = await getDocs(paymentsQuery);
    payments = payments.concat(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }
  return payments;
};

// Gift Card Functions
export const getAllGiftCards = async (): Promise<GiftCodeWithCustomer[]> => {
  try {
    const giftCardsQuery = query(
      collection(db, 'giftCodes'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(giftCardsQuery);
    
    const giftCards: GiftCodeWithCustomer[] = [];
    
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data();
      const giftCard: GiftCodeWithCustomer = {
        id: docSnapshot.id,
        ...data,
      } as GiftCodeWithCustomer;
      
      // Fetch purchaser info
      if (data.purchasedBy) {
        try {
          const purchaserDoc = await getDoc(doc(db, 'users', data.purchasedBy));
          if (purchaserDoc.exists()) {
            giftCard.purchaser = { id: purchaserDoc.id, ...purchaserDoc.data() } as User;
          }
        } catch (error) {
          console.error(`Error fetching purchaser for gift card ${docSnapshot.id}:`, error);
        }
      }
      
      // Fetch redeemer info
      if (data.redeemedBy) {
        try {
          const redeemerDoc = await getDoc(doc(db, 'users', data.redeemedBy));
          if (redeemerDoc.exists()) {
            giftCard.redeemer = { id: redeemerDoc.id, ...redeemerDoc.data() } as User;
          }
        } catch (error) {
          console.error(`Error fetching redeemer for gift card ${docSnapshot.id}:`, error);
        }
      }
      
      giftCards.push(giftCard);
    }
    
    return giftCards;
  } catch (error) {
    console.error('Error fetching gift cards:', error);
    throw error;
  }
};

export const getGiftCardAnalytics = async (): Promise<{
  totalGiftCards: number;
  totalPurchased: number;
  totalRedeemed: number;
  totalPending: number;
  totalExpired: number;
  totalCancelled: number;
  totalRevenue: number; // Total amount from purchased gift cards
  totalRedeemedValue: number; // Total value of redeemed gift cards
  averageDenomination: number;
  redemptionRate: number; // Percentage of purchased cards that were redeemed
  giftCardsByStatus: Record<string, number>;
}> => {
  try {
    const allGiftCards = await getAllGiftCards();
    
    const totalGiftCards = allGiftCards.length;
    const totalPurchased = allGiftCards.filter(gc => gc.status !== 'cancelled').length;
    const totalRedeemed = allGiftCards.filter(gc => gc.status === 'redeemed').length;
    const totalPending = allGiftCards.filter(gc => gc.status === 'pending').length;
    const totalExpired = allGiftCards.filter(gc => gc.status === 'expired').length;
    const totalCancelled = allGiftCards.filter(gc => gc.status === 'cancelled').length;
    
    // Calculate total revenue (sum of all purchased gift card denominations)
    const totalRevenue = allGiftCards
      .filter(gc => gc.status !== 'cancelled')
      .reduce((sum, gc) => sum + (gc.denomination || 0), 0);
    
    // Calculate total redeemed value
    const totalRedeemedValue = allGiftCards
      .filter(gc => gc.status === 'redeemed')
      .reduce((sum, gc) => sum + (gc.denomination || 0), 0);
    
    // Calculate average denomination
    const denominations = allGiftCards
      .filter(gc => gc.denomination)
      .map(gc => gc.denomination);
    const averageDenomination = denominations.length > 0
      ? denominations.reduce((sum, denom) => sum + denom, 0) / denominations.length
      : 0;
    
    // Calculate redemption rate
    const redemptionRate = totalPurchased > 0
      ? (totalRedeemed / totalPurchased) * 100
      : 0;
    
    // Count by status
    const giftCardsByStatus: Record<string, number> = {};
    allGiftCards.forEach(gc => {
      giftCardsByStatus[gc.status] = (giftCardsByStatus[gc.status] || 0) + 1;
    });
    
    return {
      totalGiftCards,
      totalPurchased,
      totalRedeemed,
      totalPending,
      totalExpired,
      totalCancelled,
      totalRevenue,
      totalRedeemedValue,
      averageDenomination,
      redemptionRate,
      giftCardsByStatus,
    };
  } catch (error) {
    console.error('Error fetching gift card analytics:', error);
    throw error;
  }
};